import { notesAfterDelete } from '@/lib/noteThreads';
import ProfileFlagControl from '@/components/admin/ProfileFlagControl';
import { PersonFieldsProvider, PersonFieldsToolbar } from '@/components/admin/PersonFields';
import { agencyDetails, agencyOption, selectAgency } from '@/lib/personAgency';
import { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  addStudentNote,
  updateStudentNote,
  deleteStudentNote,
  addApplicationNote,
  updateApplicationNote,
  deleteApplicationNote,
  deleteStudent,
  getAccountingCustomer,
  getAgents,
  getCourses,
  getPrograms,
  getStudent,
  getStudentsMeta,
  updateStudent,
  getStudentGPA,
  getStudentDocuments,
  getStudentDocumentDefaults,
  issueStudentDocument,
  revokeIssuedDocument,
  getIssuedDocumentDownload,
  revealStudentSin,
} from '@/lib/api';
import { accountingError, invoiceStatusMeta } from '@/lib/accounting';
import { StatusPill } from '@/components/admin/AdminTableStyles';
import { courseLabel } from '@/components/admin/AcademicTableHelpers';
import OfficialDocumentsCard from '@/components/admin/OfficialDocumentsCard';
import LanguageTestsCard from '@/components/admin/LanguageTestsCard';
import FrenchProgramSection from '@/components/admin/person/FrenchProgramSection';
import { hasFrenchAnswers } from '@/lib/frenchIntake';
import NotesThread from '@/components/admin/NotesThread';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { ProfileField as InfoRow } from '@/components/admin/ProfileField';
import {
  studentToPerson,
  joinName,
  joinPhone,
  splitPhone,
  STATUS_CODE_TO_STUDENT,
  CAMPUS_CODE_TO_STUDENT,
  ATTENDANCE_CODE_TO_STUDENT,
  PATHWAY_CODE_TO_STUDENT,
} from '@/lib/personView';
import PersonalSection from '@/components/admin/person/PersonalSection';
import StudentSections from '@/components/admin/person/StudentSections';
import {
  NONE_VALUE,
  computeNotificationDueDate,
  courseBadgeKey,
  courseBadgeLabel,
  dateChangeLabel,
  depositStatuses,
  displayText,
  documentStatuses,
  formatCurrency,
  formatDate,
  formatDateTime,
  graduationPercent,
  metaOptions,
  notificationStatuses,
  numberValue,
  safeList,
  yesNoOptions,
} from '@/components/admin/person/studentSectionHelpers';
import AgencySection from '@/components/admin/person/AgencySection';
import ReferralSection from '@/components/admin/person/ReferralSection';
import ProgramSection from '@/components/admin/person/ProgramSection';
import { useAuth } from '@/contexts/AuthContext';
import {
  ProfileLayoutToolbar,
  ProfileSection,
  ProfileSectionGrid,
  ProfileSectionPassthrough,
  useProfileLayout,
} from '@/components/admin/ProfileLayout';
import ProfileHero from '@/components/profile/ProfileHero';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { fixedRegionsForCountry, fullRegionName, regionCode, regionDisplay } from '@/lib/addressRegions';
import {
  addCalendarDays,
  formatDateTimeValue,
  formatDateValue,
  localDateInputValue,
  parseDateValue,
  toDateInputValue,
} from '@/lib/dates';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, GraduationCap,
  ArrowLeft, Globe2, Wallet, PencilLine, Save, X, BookOpen, Languages,
  CreditCard, FileDown, ExternalLink, Link2, Loader2, RefreshCw, ReceiptText, Trash2, Unlink,
  StickyNote, Send, Building2, ClipboardList, Flag, Home, FileCheck2, Ban, History, Plus, Minus,
} from 'lucide-react';
import AgencyCombobox from '@/components/admin/AgencyCombobox';

const NAVY = '#062B1A';
const GOLD = '#D4AF37';
/* The shared spine first, then the sections only a student has. One controller
   over both means a single Customize button edits the whole page while the
   spine's arrangement stays the one the admissions page shows. */
const PERSON_PAGE_SURFACES = ['person_profile', 'student_profile'];
const inputClass =
  'border-[rgba(6, 43, 26,0.16)] focus:border-[#D4AF37] focus:ring-[rgba(212, 175, 55,0.25)]';
const PROGRAM_OPTIONS = [
  'French as a Second Language (FSL)',
  'French Test Preparation (TCF & TEF)',
  'International Esports Management Diploma',
  'Hospitality Operations Diploma',
  'Global Management in Hospitality Diploma',
];
const FSL_PROGRAM_NAME = 'French as a Second Language (FSL)';
const STUDENT_DOCUMENT_DOC_TYPES = [
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'enrolment_contract', label: 'Student Enrolment Contract' },
  { value: 'loa', label: 'Official Letter of Acceptance' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'fee_payment_instructions', label: 'Fee Payment Instructions' },
];

/* ── helpers ───────────────────────────────────────── */

function genderLabel(g) {
  const map = { male: 'Male', female: 'Female', other: 'Other', prefer_not_to_say: 'Prefer not to say' };
  return map[g] || displayText(g);
}

function requiresEnrolmentContract(record, programKey = 'program_name') {
  if (typeof record?.program_requires_enrolment_contract === 'boolean') {
    return record.program_requires_enrolment_contract;
  }
  return record?.[programKey] === FSL_PROGRAM_NAME;
}

function documentTypesForContractRequirement(docTypes, enabled) {
  return enabled ? docTypes : docTypes.filter((docType) => docType.value !== 'enrolment_contract');
}



function moneyAmount(amount) {
  const value = Number.parseFloat(amount?.value ?? amount ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function formatMoney(amount) {
  const value = moneyAmount(amount);
  const currency = amount?.currency || 'CAD';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(value);
}

function cleanError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;
  const error = err?.response?.data?.error;
  if (typeof detail === 'string') return detail;
  if (typeof error === 'string') return error;
  return fallback;
}

function localDateString(date = new Date()) {
  return localDateInputValue(date);
}




function formText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function formDate(value) {
  return toDateInputValue(value);
}

function formNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount}` : '0';
}


function formBool(value) {
  return value === true ? 'true' : 'false';
}

function formSelect(value) {
  const text = formText(value);
  return text || NONE_VALUE;
}

function payloadSelect(value) {
  return value === NONE_VALUE ? '' : value;
}

/* ── Bridge to the shared person sections ──────────────

   The shared cards speak the view model from `lib/personView`: codes rather
   than display strings, a nested address, and a name in two halves. This page
   goes on editing the student's own field names, with "not set" carried as a
   sentinel because Radix treats an empty select value as absent.

   Translating here rather than rewriting the form means adopting the shared
   cards changes the markup and not the save path. */

// Read the edit form into the shared shape.
function studentFormToPerson(form) {
  if (!form) return null;
  return studentToPerson({
    first_name: form.first_name,
    last_name: form.last_name,
    name: form.name,
    preferred_name: form.preferred_name,
    email: form.email,
    phone: form.phone,
    dob: form.dob,
    gender: payloadSelect(form.gender),
    citizenship_country: payloadSelect(form.citizenship_country),
    status_in_canada: payloadSelect(form.status_in_canada),
    status_expiry_date: form.status_expiry_date,
    unit_number: form.unit_number,
    street_address: form.street_address,
    city: form.city,
    province: payloadSelect(form.province),
    country: payloadSelect(form.country),
    postal_code: form.postal_code,
    agent_id: form.agent_id,
    using_agency: form.using_agency,
    agent_first_name: form.agent_first_name,
    agent_last_name: form.agent_last_name,
    agency_company_name: form.agency_company_name,
    agent_phone: form.agent_phone,
    agent_email: form.agent_email,
    agency_notes: form.agency_notes,
    heard_about_us: form.heard_about_us,
    heard_about_us_detail: form.heard_about_us_detail,
    french_goal: form.french_goal,
    french_target_level: form.french_target_level,
    french_current_level: form.french_current_level,
    french_test_proof: form.french_test_proof,
    french_placement_preference: form.french_placement_preference,

    program_name: payloadSelect(form.program_name),
    selected_add_ons: form.selected_add_ons,
    start_date: form.start_date,
    campus: payloadSelect(form.campus),
    delivery_method: payloadSelect(form.delivery_method),
    pathway_option: payloadSelect(form.pathway_option),
    english_proficiency_type: payloadSelect(form.english_proficiency_type),
    english_proficiency_score: form.english_proficiency_score,
    application_fee_paid: form.application_fee_paid === 'true',
  });
}

// Map one edit from the shared shape back onto the form. Codes become the
// display strings a student record stores, and a cleared select becomes the
// sentinel rather than an empty string.
function applyPersonFieldToStudentForm(prev, key, value) {
  const select = (text) => (String(text || '').trim() || NONE_VALUE);
  switch (key) {
    case 'firstName':
    case 'lastName': {
      const next = {
        ...prev,
        [key === 'firstName' ? 'first_name' : 'last_name']: value,
      };
      // `name` is derived, here as on the server, so the header and every
      // read-only view follow the halves immediately.
      return { ...next, name: joinName(next.first_name, next.last_name) };
    }
    case 'preferredName': return { ...prev, preferred_name: value };
    case 'email': return { ...prev, email: value };
    case 'phoneCountryCode':
    case 'phoneNumber': {
      const current = splitPhone(prev.phone);
      const parts = { ...current, [key]: value };
      return { ...prev, phone: joinPhone(parts.phoneCountryCode, parts.phoneNumber) };
    }
    case 'dateOfBirth': return { ...prev, dob: value };
    case 'gender': return { ...prev, gender: select(value) };
    case 'citizenshipCountry': return { ...prev, citizenship_country: select(value) };
    case 'statusInCanada':
      return { ...prev, status_in_canada: select(STATUS_CODE_TO_STUDENT[value]) };
    case 'statusExpiryDate': return { ...prev, status_expiry_date: value };
    case 'program': return { ...prev, program_name: select(value) };
    case 'selectedAddOns': return { ...prev, selected_add_ons: safeList(value) };
    case 'intendedStart': return { ...prev, start_date: value };
    case 'campus': return { ...prev, campus: select(CAMPUS_CODE_TO_STUDENT[value]) };
    case 'attendanceMode':
      return { ...prev, delivery_method: select(ATTENDANCE_CODE_TO_STUDENT[value]) };
    case 'universityPathway':
      return { ...prev, pathway_option: select(PATHWAY_CODE_TO_STUDENT[value]) };
    case 'englishProficiencyType':
      return { ...prev, english_proficiency_type: select(value) };
    case 'englishProficiencyScore':
      return { ...prev, english_proficiency_score: value };
    case 'applicationFeePaid':
      return { ...prev, application_fee_paid: value ? 'true' : 'false' };
    case 'agentId': return { ...prev, agent_id: value || '' };
    case 'usingAgency':
      // Turning the switch off clears the selected agency and its contacts.
      return { ...prev, using_agency: value, ...(!value ? { agent_id: '', ...agencyDetails(null) } : {}) };
    case 'agentFirstName': return { ...prev, agent_first_name: value };
    case 'agentLastName': return { ...prev, agent_last_name: value };
    case 'agencyCompanyName': return { ...prev, agency_company_name: value };
    case 'agentPhone': return { ...prev, agent_phone: value };
    case 'agentEmail': return { ...prev, agent_email: value };
    case 'agencyNotes': return { ...prev, agency_notes: value };
    case 'heardAboutUs': return { ...prev, heard_about_us: value || '' };
    case 'heardAboutUsDetail': return { ...prev, heard_about_us_detail: value };
    case 'frenchGoal': return { ...prev, french_goal: value || '' };
    case 'frenchTargetLevel': return { ...prev, french_target_level: value || '' };
    case 'frenchCurrentLevel': return { ...prev, french_current_level: value || '' };
    case 'frenchTestProof': return { ...prev, french_test_proof: value || '' };
    case 'frenchPlacementPreference': return { ...prev, french_placement_preference: value || '' };

    default: return prev;
  }
}

const PERSON_ADDRESS_TO_STUDENT_FIELD = {
  unit: 'unit_number',
  street: 'street_address',
  city: 'city',
  provinceState: 'province',
  country: 'country',
  postalCode: 'postal_code',
};

function studentInitials(value) {
  const name = formText(value).trim();
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

/**
 * An enrolment badge names the course.
 *
 * When the course record cannot be resolved — it was removed, or this admin
 * cannot read the directory — the badge says so in words. It never falls back
 * to the stored identifier: a UUID on screen tells the reader nothing and looks
 * like a fault.
 */
const UNKNOWN_COURSE_LABEL = 'Course no longer listed';

function isoDateValue(value) {
  const text = formText(value).trim();
  const dateText = text.length >= 10 ? text.slice(0, 10) : text;
  return /^\d{4}-\d{2}-\d{2}$/.test(dateText) ? dateText : '';
}

function programKey(value) {
  return formText(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

function programIntakeDateRows(program) {
  const rows = [];
  const seen = new Set();

  const addRow = (startValue, endValue = '') => {
    const start = isoDateValue(startValue);
    if (!start || seen.has(start)) return;
    seen.add(start);
    rows.push({ start, end: isoDateValue(endValue) });
  };

  safeList(program?.intakes).forEach((item) => {
    if (typeof item === 'string') {
      addRow(item);
    } else if (item && typeof item === 'object') {
      addRow(item.start, item.end);
    }
  });
  safeList(program?.semesters).forEach((item) => addRow(item));

  return rows.sort((a, b) => a.start.localeCompare(b.start));
}


/* ── meta (countries / provinces) helpers ──────────── */

/* Map whatever is stored -- a code or a full country name -- onto the code the
   select actually offers. Without this, a record holding "Denmark" selects
   nothing and the field renders blank. */
function countryCodeFor(meta, value) {
  const text = formText(value).trim();
  if (!text) return '';
  const countries = safeList(meta?.countries);
  const byCode = countries.find((country) => country?.code === text);
  if (byCode) return byCode.code;
  const lowered = text.toLowerCase();
  const byName = countries.find((country) => String(country?.name || '').toLowerCase() === lowered);
  return byName ? byName.code : text;
}

function countryLabel(meta, code) {
  const value = formText(code).trim();
  if (!value) return '';
  const match = safeList(meta?.countries).find((country) => country?.code === value);
  return match ? match.name : value;
}

function provincesFor(meta, countryCode) {
  const value = formText(countryCode).trim();
  const fixed = fixedRegionsForCountry(value);
  if (fixed.length) return fixed;
  const country = safeList(meta?.countries).find((item) => item?.code === value);
  return safeList(country?.provinces);
}

function provinceLabel(meta, countryCode, provinceCode) {
  const value = formText(provinceCode).trim();
  if (!value) return '';
  return regionDisplay(countryCode, value);
}

const ADDRESS_FIELDS = ['unit_number', 'street_address', 'city', 'province', 'country', 'postal_code'];

function hasStructuredAddress(student) {
  return ADDRESS_FIELDS.some((key) => formText(student?.[key]).trim() !== '');
}


/* ── select options ────────────────────────────────── */

const genderOptions = [
  { value: NONE_VALUE, label: 'Not set' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];




const issuedDocTypes = [
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'loa', label: 'Official Letter of Acceptance' },
  { value: 'transcript', label: 'Transcript' },
];

const checklistStatusOptions = ['Received', 'Exempted', 'Pending'];

const proficiencyTypes = [
  { value: NONE_VALUE, label: 'Not set' },
  { value: 'direct_entry', label: 'Direct Entry' },
  { value: 'ielts', label: 'IELTS' },
  { value: 'toefl', label: 'TOEFL' },
  { value: 'duolingo', label: 'Duolingo' },
  { value: 'mcc_test', label: 'MCC English Test' },
];


// Shape returned by GET /api/students/meta. Used as the default so selects
// degrade to a single "Not set" option if the fetch fails.
const EMPTY_META = {
  status_in_canada: [],
  campus: [],
  delivery_method: [],
  pathway_option: [],
  gua: [],
  enrollment_status: [],
  programs: PROGRAM_OPTIONS,
  countries: [],
  provinces: [],
};

// Build [{ value, label }] options from a meta enum list, prepending "Not set".

function campusFormValue(value) {
  const campus = formSelect(value);
  const legacy = {
    'Vancouver - Downtown': 'Vancouver (Downtown)',
    'Any campus': 'Undecided',
    Other: 'Undecided',
    'TBA (new campus opening soon)': 'Undecided',
  };
  return legacy[campus] || campus || 'Undecided';
}

function programOptionsWithLegacy(programs, selectedProgram) {
  const canonical = safeList(programs).filter(Boolean);
  const current = payloadSelect(selectedProgram).trim();
  const legacy = current && !canonical.includes(current) ? [{ value: current, label: `${current} (legacy)` }] : [];
  return [
    { value: NONE_VALUE, label: 'Not set' },
    ...canonical.map((program) => ({ value: program, label: program })),
    ...legacy,
  ];
}

/* ── form builder ──────────────────────────────────── */

function makeForm(s, meta) {
  const student = s || {};
  return {
    custom_fields: { ...(student.custom_fields || {}) },
    student_number: formText(student.student_number),
    // `name` stays the display value; the halves are what the admissions
    // record round-trips without re-guessing where the given name ends.
    first_name: formText(student.first_name),
    last_name: formText(student.last_name),
    name: formText(student.name),
    email: formText(student.email),
    phone: formText(student.phone),
    address: formText(student.address),
    photo: formText(student.photo),
    dob: formDate(student.dob),
    gender: formSelect(student.gender),
    sin: formText(student.sin),
    tuition_total: formNumber(student.tuition_total),
    tuition_paid: formNumber(student.tuition_paid),
    has_student_loan: formBool(student.has_student_loan),
    loan_total: formNumber(student.loan_total),
    loan_paid: formNumber(student.loan_paid),
    start_date: formDate(student.start_date),
    end_date: formDate(student.end_date),
    graduation_notification_due_date:
      formDate(student.graduation_notification_due_date) || computeNotificationDueDate(formDate(student.end_date)),
    graduation_notification_status: formText(student.graduation_notification_status) || 'not_needed',
    graduation_notification_sent_at: formDate(student.graduation_notification_sent_at),
    graduation_notification_acknowledged_at: formDate(student.graduation_notification_acknowledged_at),
    current_grade_summary: formText(student.current_grade_summary),
    graduation_ready: formBool(student.graduation_ready),
    graduation_ready_checked_at: formDate(student.graduation_ready_checked_at),
    graduation_ready_checked_by: formText(student.graduation_ready_checked_by),
    last_term_notes: formText(student.last_term_notes),
    is_international: formBool(student.is_international),
    study_permit_status: formText(student.study_permit_status) || 'not_required',
    study_permit_expiry: formDate(student.study_permit_expiry),
    visa_status: formText(student.visa_status) || 'not_required',
    visa_expiry: formDate(student.visa_expiry),
    compliance_verified_at: formDate(student.compliance_verified_at),
    compliance_notes: formText(student.compliance_notes),
    deposit_required: formBool(student.deposit_required),
    deposit_amount: formNumber(student.deposit_amount),
    deposit_due_date: formDate(student.deposit_due_date),
    deposit_paid_date: formDate(student.deposit_paid_date),
    deposit_status: formText(student.deposit_status) || 'not_required',
    deposit_notes: formText(student.deposit_notes),
    program_name: formSelect(student.program_name),
    selected_add_ons: safeList(student.selected_add_ons),
    english_proficiency_type: formSelect(student.english_proficiency_type),
    english_proficiency_score: formText(student.english_proficiency_score),
    application_fee_paid: formBool(student.application_fee_paid),
    // ── Profile structure expansion ──
    preferred_name: formText(student.preferred_name),
    citizenship_country: formSelect(countryCodeFor(meta, student.citizenship_country)),
    status_in_canada: formSelect(student.status_in_canada),
    status_expiry_date: formDate(student.status_expiry_date),
    unit_number: formText(student.unit_number),
    street_address: formText(student.street_address),
    city: formText(student.city),
    province: formSelect(student.province),
    country: formSelect(countryCodeFor(meta, student.country)),
    postal_code: formText(student.postal_code),
    campus: campusFormValue(student.campus),
    delivery_method: formSelect(student.delivery_method),
    pathway_option: formSelect(student.pathway_option),
    gua: formSelect(student.gua),
    enrollment_status: formSelect(student.enrollment_status),
    agent_id: formText(student.agent_id),
    using_agency: Boolean(student.using_agency || student.agent_id),
    agent_first_name: formText(student.agent_first_name),
    agent_last_name: formText(student.agent_last_name),
    agency_company_name: formText(student.agency_company_name),
    agent_phone: formText(student.agent_phone),
    agent_email: formText(student.agent_email),
    agency_notes: formText(student.agency_notes),
    heard_about_us: formText(student.heard_about_us),
    heard_about_us_detail: formText(student.heard_about_us_detail),
    french_goal: formText(student.french_goal),
    french_target_level: formText(student.french_target_level),
    french_current_level: formText(student.french_current_level),
    french_test_proof: formText(student.french_test_proof),
    french_placement_preference: formText(student.french_placement_preference),

  };
}

function buildPayload(f) {
  return {
    custom_fields: f.custom_fields || {},
    student_number: f.student_number.trim().toUpperCase(),
    first_name: f.first_name.trim(),
    last_name: f.last_name.trim(),
    name: f.name.trim(),
    email: f.email.trim(),
    phone: f.phone.trim(),
    address: f.address.trim(),
    photo: f.photo.trim(),
    dob: f.dob,
    gender: payloadSelect(f.gender),
    sin: f.sin.trim(),
    has_student_loan: f.has_student_loan === 'true',
    loan_total: Number(f.loan_total) || 0,
    loan_paid: Number(f.loan_paid) || 0,
    start_date: f.start_date,
    end_date: f.end_date,
    graduation_notification_due_date: f.graduation_notification_due_date,
    graduation_notification_status: f.graduation_notification_status,
    graduation_notification_sent_at: f.graduation_notification_sent_at,
    graduation_notification_acknowledged_at: f.graduation_notification_acknowledged_at,
    current_grade_summary: f.current_grade_summary.trim(),
    graduation_ready: f.graduation_ready === 'true',
    graduation_ready_checked_at: f.graduation_ready_checked_at,
    graduation_ready_checked_by: f.graduation_ready_checked_by.trim(),
    last_term_notes: f.last_term_notes.trim(),
    is_international: f.is_international === 'true',
    study_permit_status: f.study_permit_status,
    study_permit_expiry: f.study_permit_expiry,
    visa_status: f.visa_status,
    visa_expiry: f.visa_expiry,
    compliance_verified_at: f.compliance_verified_at,
    compliance_notes: f.compliance_notes.trim(),
    deposit_required: f.deposit_required === 'true',
    deposit_amount: Number(f.deposit_amount) || 0,
    deposit_due_date: f.deposit_due_date,
    deposit_paid_date: f.deposit_paid_date,
    deposit_status: f.deposit_status,
    deposit_notes: f.deposit_notes.trim(),
    program_name: payloadSelect(f.program_name).trim(),
    selected_add_ons: safeList(f.selected_add_ons),
    english_proficiency_type: payloadSelect(f.english_proficiency_type),
    english_proficiency_score: f.english_proficiency_score.trim(),
    application_fee_paid: f.application_fee_paid === 'true',
    // ── Profile structure expansion ──
    preferred_name: f.preferred_name.trim(),
    citizenship_country: payloadSelect(f.citizenship_country),
    status_in_canada: payloadSelect(f.status_in_canada),
    status_expiry_date: f.status_expiry_date,
    unit_number: f.unit_number.trim(),
    street_address: f.street_address.trim(),
    city: f.city.trim(),
    province: regionCode(payloadSelect(f.country), payloadSelect(f.province)),
    country: payloadSelect(f.country),
    postal_code: f.postal_code.trim(),
    campus: payloadSelect(f.campus),
    delivery_method: payloadSelect(f.delivery_method),
    pathway_option: payloadSelect(f.pathway_option),
    gua: payloadSelect(f.gua),
    enrollment_status: payloadSelect(f.enrollment_status),
    agent_id: f.using_agency ? f.agent_id || '' : '',
    using_agency: Boolean(f.using_agency),
    agent_first_name: f.agent_first_name || '',
    agent_last_name: f.agent_last_name || '',
    agency_company_name: f.agency_company_name || '',
    agent_phone: f.agent_phone || '',
    agent_email: f.agent_email || '',
    agency_notes: f.agency_notes || '',
    // The server has the last word here: linking an agency forces the source to
    // "agency" whatever this form sends (_heard_about_for_agency_state).
    heard_about_us: f.using_agency ? 'agency' : f.heard_about_us || '',
    heard_about_us_detail: f.heard_about_us_detail || '',
    // Sent as answered; the server re-derives the tree and clears whatever the
    // answers above no longer ask for (french_intake.normalize_answers).
    french_goal: f.french_goal || '',
    french_target_level: f.french_target_level || '',
    french_current_level: f.french_current_level || '',
    french_test_proof: f.french_test_proof || '',
    french_placement_preference: f.french_placement_preference || '',

  };
}

function normalizedOriginalValue(key, value, nextValue) {
  if (Array.isArray(nextValue)) return Array.isArray(value) ? value : [];
  if (typeof nextValue === 'boolean') return value === true;
  if (typeof nextValue === 'number') {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }
  if (
    key === 'dob' ||
    key.endsWith('_date') ||
    key.endsWith('_at') ||
    key.endsWith('_expiry')
  ) {
    return formDate(value);
  }
  return formText(value).trim();
}

function buildPatch(form, original) {
  const next = buildPayload(form);
  return Object.entries(next).reduce((patch, [key, value]) => {
    if (key === 'custom_fields') {
      const changed = Object.fromEntries(Object.entries(value).filter(([id, answer]) => answer !== original?.custom_fields?.[id]));
      if (Object.keys(changed).length) patch.custom_fields = changed;
      return patch;
    }
    const originalValue = normalizedOriginalValue(key, original?.[key], value);
    if (Array.isArray(value) ? JSON.stringify(value) !== JSON.stringify(originalValue) : value !== originalValue) {
      patch[key] = value;
    }
    return patch;
  }, {});
}

/* ── sub-components ────────────────────────────────── */


function FieldError({ error }) {
  if (!error) return null;
  return <p className="mt-1 text-xs text-red-600">{error}</p>;
}

function issuedDocLabel(docType) {
  return issuedDocTypes.find((doc) => doc.value === docType)?.label || 'Document';
}

function newestDocument(history) {
  return safeList(history).reduce((latest, doc) => {
    if (!latest) return doc;
    return Number(doc?.version || 0) > Number(latest?.version || 0) ? doc : latest;
  }, null);
}

function StudentDocumentsCard({ studentId }) {
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState({});
  const [issueType, setIssueType] = useState('');
  const [issueInputs, setIssueInputs] = useState({});
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState('');
  const [revokingId, setRevokingId] = useState('');

  const loadDocuments = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      setError('');
      const res = await getStudentDocuments(studentId);
      setDocuments(res.data || {});
    } catch (err) {
      console.error(err);
      setError(cleanError(err, 'Documents could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const updateIssueInput = (key, value) => {
    setIssueInputs((current) => ({ ...current, [key]: value }));
  };

  const updateListRow = (key, index, field, value) => {
    setIssueInputs((current) => {
      const rows = safeList(current[key]).map((row) => ({ ...row }));
      rows[index] = { ...(rows[index] || {}), [field]: value };
      return { ...current, [key]: rows };
    });
  };

  const addListRow = (key, row) => {
    setIssueInputs((current) => ({ ...current, [key]: [...safeList(current[key]), row] }));
  };

  const removeListRow = (key, index) => {
    setIssueInputs((current) => ({
      ...current,
      [key]: safeList(current[key]).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const openIssueDialog = async (docType) => {
    setIssueType(docType);
    setIssueInputs({});
    setIssueLoading(true);
    try {
      const res = await getStudentDocumentDefaults(studentId, docType);
      setIssueInputs(res.data?.inputs || {});
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document defaults could not be loaded.'));
      setIssueType('');
    } finally {
      setIssueLoading(false);
    }
  };

  const closeIssueDialog = () => {
    if (issueSubmitting) return;
    setIssueType('');
    setIssueInputs({});
  };

  const handleIssue = async () => {
    if (!issueType) return;
    const active = documents?.[issueType]?.active;
    if (active && !window.confirm(`Re-issue ${issuedDocLabel(issueType)}? The previous active version will be revoked for the student.`)) {
      return;
    }

    try {
      setIssueSubmitting(true);
      await issueStudentDocument(studentId, { doc_type: issueType, inputs: issueInputs });
      toast.success(`${issuedDocLabel(issueType)} issued.`);
      setIssueType('');
      setIssueInputs({});
      await loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document could not be issued.'));
    } finally {
      setIssueSubmitting(false);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      setDownloadingId(documentId);
      const res = await getIssuedDocumentDownload(documentId);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Download link could not be created.'));
    } finally {
      setDownloadingId('');
    }
  };

  const handleRevoke = async (doc) => {
    if (!doc?.id) return;
    if (!window.confirm('Revoke this document? The student will lose access immediately, but admins can still download the archived version.')) {
      return;
    }
    try {
      setRevokingId(doc.id);
      await revokeIssuedDocument(doc.id);
      toast.success(`${issuedDocLabel(doc.doc_type)} revoked.`);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document could not be revoked.'));
    } finally {
      setRevokingId('');
    }
  };

  const renderTextInput = (key, label, type = 'text') => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type={type}
        value={type === 'date' ? formDate(issueInputs[key]) : formText(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
      />
    </div>
  );

  const renderNumberInput = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={formNumber(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
      />
    </div>
  );

  const renderTextarea = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Textarea
        value={formText(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
        rows={3}
      />
    </div>
  );

  const renderBooleanSelect = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={formBool(issueInputs[key])} onValueChange={(value) => updateIssueInput(key, value === 'true')}>
        <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
        <SelectContent>
          {yesNoOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const renderChecklist = () => {
    const rows = safeList(issueInputs.document_checklist);
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>Document Checklist</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => addListRow('document_checklist', { label: '', status: 'Pending' })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] px-3 py-4 text-sm text-[#062B1A]/45">
            No checklist rows.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={`${row.label || 'checklist'}-${index}`} className="grid grid-cols-1 gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] p-3 sm:grid-cols-[1fr_170px_auto]">
                <Input
                  value={formText(row.label)}
                  onChange={(e) => updateListRow('document_checklist', index, 'label', e.target.value)}
                  placeholder="Requirement"
                  className={inputClass}
                />
                <Select value={formText(row.status) || 'Pending'} onValueChange={(value) => updateListRow('document_checklist', index, 'status', value)}>
                  <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {checklistStatusOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => removeListRow('document_checklist', index)} aria-label="Remove checklist row">
                  <Minus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFeeRows = (key, label) => {
    const rows = safeList(issueInputs[key]);
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>{label}</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => addListRow(key, { amount: 0, label: '' })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] px-3 py-4 text-sm text-[#062B1A]/45">
            No fee rows.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={`${key}-${index}`} className="grid grid-cols-1 gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] p-3 sm:grid-cols-[140px_1fr_auto]">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formNumber(row.amount)}
                  onChange={(e) => updateListRow(key, index, 'amount', e.target.value)}
                  className={inputClass}
                />
                <Input
                  value={formText(row.label)}
                  onChange={(e) => updateListRow(key, index, 'label', e.target.value)}
                  placeholder="Label"
                  className={inputClass}
                />
                <Button type="button" variant="outline" size="icon" onClick={() => removeListRow(key, index)} aria-label={`Remove ${label} row`}>
                  <Minus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderIssueForm = () => {
    if (issueLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-[#062B1A]/55">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading defaults...
        </div>
      );
    }

    if (issueType === 'offer_letter') {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
            {renderTextInput('application_number', 'Application Number')}
          </div>
          {renderTextarea('prerequisites_condition', 'Prerequisites Condition')}
          {renderTextarea('scholarship_bursary', 'Scholarship / Bursary')}
          {renderChecklist()}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {renderFeeRows('min_fees', 'Minimum Fees')}
            {renderFeeRows('recommended_fees', 'Recommended Fees')}
          </div>
        </div>
      );
    }

    if (issueType === 'loa') {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
            {renderTextInput('field_of_study', 'Field of Study')}
            {renderTextInput('level_of_study', 'Level of Study')}
            {renderTextInput('length_of_program', 'Length of Program')}
            {renderNumberInput('hours_per_week', 'Hours per Week')}
            {renderNumberInput('estimated_first_year_tuition', 'Estimated First Year Tuition')}
            {renderNumberInput('prepaid_tuition', 'Prepaid Tuition')}
            {renderNumberInput('scholarship_amount', 'Scholarship Amount')}
            {renderBooleanSelect('caq', 'CAQ')}
            {renderBooleanSelect('conditions', 'Conditions')}
          </div>
          {renderTextarea('prerequisites_condition', 'Prerequisites Condition')}
          {renderTextarea('pathway_opportunity', 'Pathway Opportunity')}
          {renderTextarea('conditions_text', 'Conditions Text')}
          {renderTextarea('other_relevant_info', 'Other Relevant Info')}
        </div>
      );
    }

    if (issueType === 'transcript') {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
            {renderTextInput('academic_standing', 'Academic Standing')}
            {renderTextInput('cumulative_standing', 'Cumulative Standing')}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
            <FileCheck2 className="w-4 h-4 text-[#D4AF37]" /> Documents
          </CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={loadDocuments} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[#062B1A]/55">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading documents...
          </div>
        ) : (
          issuedDocTypes.map((docType) => {
            const group = documents?.[docType.value] || { label: docType.label, active: null, history: [] };
            const active = group.active;
            const history = safeList(group.history);
            const latest = newestDocument(history);
            const isHistoryOpen = Boolean(historyOpen[docType.value]);
            const statusText = active
              ? `Issued v${active.version || 1} on ${formatDate(active.issued_at)}`
              : latest?.status === 'revoked'
                ? `Revoked on ${formatDate(latest.revoked_at)}`
                : 'Not issued';
            const badgeClass = active
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : latest?.status === 'revoked'
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-[#062B1A]/15 bg-[#F7F8FA] text-[#062B1A]/55';

            return (
              <div key={docType.value} className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-white/70 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-[#062B1A]">{docType.label}</p>
                      <Badge variant="outline" className={badgeClass}>{statusText}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-[#062B1A]/45">
                      {active
                        ? `Issued by ${displayText(active.issued_by_name, 'Admin')}${active.file_name ? ` - ${active.file_name}` : ''}`
                        : history.length
                          ? `${history.length} archived version${history.length === 1 ? '' : 's'}`
                          : 'No official version has been issued yet.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={() => openIssueDialog(docType.value)} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                      <FileCheck2 className="w-4 h-4 mr-2" /> {active ? 'Re-issue' : 'Issue'}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(active.id)} disabled={!active || downloadingId === active?.id}>
                      {downloadingId === active?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                      Download
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleRevoke(active)} disabled={!active || revokingId === active?.id}>
                      {revokingId === active?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                      Revoke
                    </Button>
                    {history.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryOpen((current) => ({ ...current, [docType.value]: !current[docType.value] }))}
                      >
                        <History className="w-4 h-4 mr-2" /> History
                      </Button>
                    )}
                  </div>
                </div>
                {isHistoryOpen && history.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-[rgba(6,43,26,0.08)] pt-3">
                    {history.map((doc) => (
                      <div key={doc.id} className="flex flex-col gap-2 rounded-lg bg-[#F7F8FA] px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-[#062B1A]">
                            v{doc.version || 1} - {doc.status === 'issued' ? 'Issued' : 'Revoked'}
                          </p>
                          <p className="text-xs text-[#062B1A]/45">
                            {[formatDateTime(doc.issued_at), doc.status === 'revoked' ? `revoked ${formatDateTime(doc.revoked_at)}` : '', doc.file_name].filter(Boolean).join(' - ')}
                          </p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(doc.id)} disabled={downloadingId === doc.id}>
                          {downloadingId === doc.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>

      <Dialog open={Boolean(issueType)} onOpenChange={(open) => (open ? null : closeIssueDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{issueType ? `Issue ${issuedDocLabel(issueType)}` : 'Issue Document'}</DialogTitle>
            <DialogDescription>
              Review the generated defaults and adjust only the editable issuance fields.
            </DialogDescription>
          </DialogHeader>
          {renderIssueForm()}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeIssueDialog} disabled={issueSubmitting}>Cancel</Button>
            <Button type="button" onClick={handleIssue} disabled={issueLoading || issueSubmitting} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {issueSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
              {documents?.[issueType]?.active ? 'Re-issue Document' : 'Issue Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

class StudentDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Student detail render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => window.history.back()} className="text-[#062B1A] -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Card className="border border-[rgba(185,28,28,0.18)] bg-red-50/70">
            <CardHeader>
              <CardTitle className="text-base text-red-800">Student detail could not render</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-red-700">
              Refresh the page or return to the student list. The page caught a bad field instead of showing a blank screen.
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════ */

function AdminStudentDetailContent() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || '';
  const layoutController = useProfileLayout(PERSON_PAGE_SURFACES);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [programRecords, setProgramRecords] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [agents, setAgents] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');
  const [revealedSin, setRevealedSin] = useState('');
  const [revealingSin, setRevealingSin] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteAdmissionRecord, setDeleteAdmissionRecord] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [studentRes, coursesRes, programsRes] = await Promise.all([
          getStudent(studentId),
          getCourses().catch((err) => {
            console.error('Failed to load courses for enrollment labels', err);
            return { data: [] };
          }),
          getPrograms().catch((err) => {
            console.error('Failed to load programs for graduation dates', err);
            return { data: [] };
          }),
        ]);
        setStudent(studentRes.data);
        setCourses(coursesRes.data || []);
        setProgramRecords(safeList(programsRes.data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [studentId]);

  // Meta + agents are non-critical; the render error boundary does NOT catch
  // async rejections, so swallow them here (toast + empty options).
  useEffect(() => {
    getStudentsMeta()
      .then((res) => setMeta({ ...EMPTY_META, ...(res.data || {}) }))
      .catch(() => toast.error('Could not load profile options — selects may be empty'));
    getAgents()
      .then((res) => setAgents(safeList(res.data)))
      .catch(() => toast.error('Could not load agencies'));
  }, []);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => {
      if (course?.id) map[course.id] = course;
    });
    return map;
  }, [courses]);
  const studentDocumentDocTypes = useMemo(
    () => documentTypesForContractRequirement(
      STUDENT_DOCUMENT_DOC_TYPES,
      requiresEnrolmentContract(student),
    ),
    [student],
  );

  /* ── edit helpers ─────────────────────────────────── */

  const startEditing = () => {
    setForm(() => {
      const draft = makeForm(student, meta);
      const details = agencyDetails(agents.find((agent) => agent.id === student.agent_id));
      Object.entries(details).forEach(([key, value]) => { if (!draft[key]) draft[key] = value; });
      return draft;
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setForm(null);
  };

  const setText = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const setSelect = (key) => (value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setGraduationStartDate = (value, intakeOptions = []) => {
    const selectedIntake = intakeOptions.find((item) => item.start === value);
    setForm((prev) => {
      const next = { ...prev, start_date: value };
      if (selectedIntake && !selectedIntake.legacy) {
        next.end_date = selectedIntake.end || '';
        const previousAutoDue = computeNotificationDueDate(prev.end_date);
        if (!prev.graduation_notification_due_date || prev.graduation_notification_due_date === previousAutoDue) {
          next.graduation_notification_due_date = computeNotificationDueDate(next.end_date);
        }
      }
      return next;
    });
  };

  const setGraduationEndDate = (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const previousAutoDue = computeNotificationDueDate(prev.end_date);
      const next = { ...prev, end_date: value };
      if (!prev.graduation_notification_due_date || prev.graduation_notification_due_date === previousAutoDue) {
        next.graduation_notification_due_date = computeNotificationDueDate(value);
      }
      return next;
    });
  };

  // Changing country invalidates any previously chosen province.
  const setCountry = (value) =>
    setForm((prev) => ({ ...prev, country: value, province: NONE_VALUE }));

  const handleAddNote = async (text, parentId, parentNote) => {
    const body = String(text || '').trim();
    if (!body) { toast.error('Write a note first'); return; }
    setAddingNote(true);
    try {
      const currentStudentId = student.id || studentId;
      const ownerType = parentNote?.ownerType || 'student';
      const ownerId = parentNote?.ownerId || currentStudentId;
      const res = ownerType === 'application'
        ? await addApplicationNote(ownerId, {
          body,
          parent_id: parentId || null,
          source_type: 'student',
          source_id: currentStudentId,
        })
        : await addStudentNote(ownerId, {
          text: body,
          parent_id: parentId || null,
          source_type: 'student',
          source_id: currentStudentId,
        });
      setStudent((prev) => {
        const history = [...safeList(prev?.note_history), res.data];
        // Mirror the server-side bump so the replied-to thread jumps to the top
        // without waiting for a refetch.
        if (parentId) {
          return {
            ...prev,
            note_history: history.map((note) => (
              note.id === parentId ? { ...note, last_activity_at: res.data.created_at } : note
            )),
          };
        }
        return { ...prev, note_history: history };
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not add note');
      throw err;
    } finally {
      setAddingNote(false);
    }
  };

  const handleEditNote = async (note, text) => {
    try {
      const ownerType = note.ownerType || 'student';
      const ownerId = note.ownerId || student.id || studentId;
      const res = ownerType === 'application'
        ? await updateApplicationNote(ownerId, note.id, { body: text })
        : await updateStudentNote(ownerId, note.id, { text });
      setStudent((prev) => ({
        ...prev,
        note_history: safeList(prev?.note_history).map((item) => (
          item.id === note.id ? res.data : item
        )),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update the note');
      throw err;
    }
  };

  const handleDeleteNote = async (note) => {
    setDeletingNoteId(note.id);
    try {
      const ownerType = note.ownerType || 'student';
      const ownerId = note.ownerId || student.id || studentId;
      if (ownerType === 'application') await deleteApplicationNote(ownerId, note.id);
      else await deleteStudentNote(ownerId, note.id);
      setStudent((prev) => ({
        ...prev,
        note_history: notesAfterDelete(prev?.note_history, note.id),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete the note');
    } finally {
      setDeletingNoteId('');
    }
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.student_number.trim()) { toast.error('Student ID is required'); return; }
    if (!form.name.trim()) { toast.error('Student name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error('Valid email required'); return; }
    const patch = buildPatch(form, student);
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      cancelEditing();
      return;
    }
    try {
      setSaving(true);
      const res = await updateStudent(student.id || studentId, patch);
      setStudent(res.data);
      toast.success(`Updated ${res.data.name}`);
      setEditing(false);
      setForm(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleRevealSin = async () => {
    try {
      setRevealingSin(true);
      const res = await revealStudentSin(student.id || studentId);
      setRevealedSin(res.data?.sin || '');
      toast.success('SIN revealed');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not reveal SIN');
    } finally {
      setRevealingSin(false);
    }
  };

  const handleConfirmedDelete = async () => {
    try {
      setDeleting(true);
      await deleteStudent(
        student.id || studentId,
        deleteAdmissionRecord ? { delete_application_record: true } : undefined,
      );
      toast.success(deleteAdmissionRecord
        ? 'Student and admission record deleted'
        : 'Student deleted - admission record can be converted again or deleted');
      navigate('/admin/students');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete the student');
      setDeleting(false);
    }
  };

  /* ── loading / not-found ─────────────────────────── */

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-[#F7F8FA] animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!student) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/students')} className="text-[#062B1A]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to students
        </Button>
        <p className="text-[#062B1A]/60">Student not found.</p>
      </div>
    );
  }

  /* ── derived values (use form values while editing) ── */

  const sStartDate = editing ? form.start_date : formDate(student.start_date);
  const sEndDate = editing ? form.end_date : formDate(student.end_date);
  const sTuitionTotal = editing ? numberValue(form.tuition_total) : numberValue(student.tuition_total);
  const sTuitionPaid = editing ? numberValue(form.tuition_paid) : numberValue(student.tuition_paid);
  const sHasLoan = editing ? form.has_student_loan === 'true' : student.has_student_loan === true;
  const sLoanTotal = editing ? numberValue(form.loan_total) : numberValue(student.loan_total);
  const sLoanPaid = editing ? numberValue(form.loan_paid) : numberValue(student.loan_paid);
  const sIsIntl = editing ? form.is_international === 'true' : student.is_international === true;
  const enrolledCourses = safeList(student.enrolled_courses);
  const gradeSnapshot = student.grade_snapshot && typeof student.grade_snapshot === 'object' ? student.grade_snapshot : {};
  const linkedAdmissionId = formText(student.source_application_id || student.application_id || student.applicationId).trim();

  const gradPct = graduationPercent(sStartDate, sEndDate);
  const tuitionRemaining = Math.max(0, sTuitionTotal - sTuitionPaid);
  const loanRemaining = Math.max(0, sLoanTotal - sLoanPaid);

  // Notes are stored append-order (oldest first); show newest first.
  const noteHistory = safeList(student.note_history).map((note) => ({
    ...note,
    id: note.id,
    text: note.text ?? note.body ?? '',
    author: note.author_name || note.author || 'Staff member',
    authorId: note.author_id || note.author_user_id || note.authorId || '',
    createdAt: note.created_at || note.createdAt,
    parentId: note.parent_id || note.parentId || null,
    mentions: note.mentions || [],
    lastActivityAt: note.last_activity_at || note.lastActivityAt || note.created_at || note.createdAt,
    editedAt: note.edited_at || note.editedAt,
    sourceLabel: note.source_label || note.sourceLabel || '',
    sourceType: note.source_type || note.sourceType || '',
    sourceId: note.source_id || note.sourceId || '',
    sourceHref: note.source_href || note.sourceHref || '',
    ownerType: note.owner_type || note.ownerType || 'student',
    ownerId: note.owner_id || note.ownerId || student.id || studentId,
  }));

  // Agency link (read mode) — resolve the linked agency's display name.
  const linkedAgentId = formText(editing ? form?.agent_id : student.agent_id).trim();
  const linkedAgent = agents.find((agent) => agent.id === linkedAgentId);
  const linkedAgentName = linkedAgent
    ? (linkedAgent.company_name || linkedAgent.preferredName || linkedAgent.agent_name || linkedAgentId)
    : linkedAgentId;

  // Agency searchable select (edit mode).
  // The combobox does its own filtering, so the full list goes to it.
  const agentOptions = safeList(agents)
    .map(agencyOption);
  // Keep the currently selected agency visible even if the search filters it out.
  if (editing && form?.agent_id && !agentOptions.some((option) => option.id === form.agent_id)) {
    const selected = safeList(agents).find((agent) => agent.id === form.agent_id);
    if (selected) {
      agentOptions.unshift({
        id: selected.id,
        label: agencyOption(selected).label,
        searchText: '',
      });
    }
  }

  const programOptions = programOptionsWithLegacy(meta.programs, editing ? form?.program_name : student.program_name);
  const selectedProgramName = payloadSelect(editing && form ? form.program_name : student.program_name).trim();
  const selectedProgramRecord = safeList(programRecords).find((program) => programKey(program?.name) === programKey(selectedProgramName));
  const availableAddOns = safeList(selectedProgramRecord?.add_ons).filter((item) => item?.id && item?.label);
  const selectedAddOnLabels = availableAddOns
    .filter((item) => safeList(student.selected_add_ons).includes(item.id))
    .map((item) => item.label)
    .join(', ') || 'None';
  const setStudentProgram = (value) => {
    const nextProgram = safeList(programRecords).find((program) => programKey(program?.name) === programKey(value));
    const allowed = new Set(safeList(nextProgram?.add_ons).map((item) => item?.id).filter(Boolean));
    setForm((prev) => ({ ...prev, program_name: value, selected_add_ons: safeList(prev?.selected_add_ons).filter((id) => allowed.has(id)) }));
  };
  const toggleStudentAddOn = (id, enabled) => setForm((prev) => {
    const selected = new Set(safeList(prev?.selected_add_ons));
    if (enabled) selected.add(id); else selected.delete(id);
    const extraEnds = availableAddOns
      .filter((item) => selected.has(item.id))
      .flatMap((item) => safeList(item.end_dates))
      .filter((row) => row?.start === prev.start_date && row?.end)
      .map((row) => row.end);
    const regularEnd = programIntakeDateRows(selectedProgramRecord)
      .find((row) => row.start === prev.start_date)?.end || '';
    return {
      ...prev,
      selected_add_ons: [...selected],
      end_date: extraEnds.length ? extraEnds.sort().at(-1) : regularEnd || prev.end_date,
    };
  });
  const configuredIntakeOptions = programIntakeDateRows(selectedProgramRecord).map((intake) => {
    const selected = new Set(safeList(editing ? form?.selected_add_ons : student.selected_add_ons));
    const extraEnds = availableAddOns
      .filter((item) => selected.has(item.id))
      .flatMap((item) => safeList(item.end_dates))
      .filter((row) => row?.start === intake.start && row?.end)
      .map((row) => row.end);
    return { ...intake, end: extraEnds.length ? extraEnds.sort().at(-1) : intake.end };
  });
  const currentStartDate = editing && form ? form.start_date : formDate(student.start_date);
  const intakeDateOptions = currentStartDate && !configuredIntakeOptions.some((item) => item.start === currentStartDate)
    ? [{ start: currentStartDate, end: formDate(student.end_date), legacy: true }, ...configuredIntakeOptions]
    : configuredIntakeOptions;
  const dateChangeHistory = [...safeList(student.date_change_history)].reverse();
  const formProvinces = editing && form ? provincesFor(meta, payloadSelect(form.country)) : [];
  const formProvinceName = editing && form
    ? regionDisplay(payloadSelect(form.country), payloadSelect(form.province))
    : '';
  const countrySelectOptions = [
    { value: NONE_VALUE, label: 'Not set' },
    ...safeList(meta.countries).map((country) => ({ value: country.code, label: country.name })),
  ];
  [form?.country, form?.citizenship_country].forEach((value) => {
    const code = payloadSelect(value);
    if (code && !countrySelectOptions.some((option) => option.value === code)) {
      countrySelectOptions.push({ value: code, label: code });
    }
  });

  /* ── What the shared person sections are handed ──────── */

  // Plain derivations, not memos: everything below here sits after the page's
  // loading and not-found returns, so a hook would be a conditional one.
  const personView = studentToPerson(student);
  const personForm = studentFormToPerson(form);
  /* Shown for a French programme, and for any record that already carries an
     answer — changing the programme afterwards does not unsay what they told
     us on the application. */
  const showFrenchSection = Boolean(selectedProgramRecord?.french_questions_enabled)
    || hasFrenchAnswers(personView) || hasFrenchAnswers(personForm);

  const setPersonField = (key, value) => {
    if (key === 'program') {
      // Changing programme drops add-ons the new one does not offer, which is
      // more than a field assignment.
      setStudentProgram(value);
      return;
    }
    if (key === 'selectedAddOns') {
      // The end date follows the add-ons, so go through the existing toggle
      // rather than assigning the list.
      const current = new Set(safeList(form?.selected_add_ons));
      const next = new Set(safeList(value));
      availableAddOns.forEach((addOn) => {
        if (current.has(addOn.id) !== next.has(addOn.id)) {
          toggleStudentAddOn(addOn.id, next.has(addOn.id));
        }
      });
      return;
    }
    setForm((prev) => applyPersonFieldToStudentForm(prev, key, value));
  };

  const setPersonAddressField = (key, value) => {
    const target = PERSON_ADDRESS_TO_STUDENT_FIELD[key];
    if (!target) return;
    setForm((prev) => {
      if (key !== 'country') return { ...prev, [target]: value || NONE_VALUE };
      // A province belongs to its country, so changing the country clears a
      // province that no longer belongs to it.
      const changed = payloadSelect(prev.country) !== value;
      return {
        ...prev,
        country: value || NONE_VALUE,
        province: changed ? NONE_VALUE : prev.province,
      };
    });
  };

  // The shared Program card takes the same option shapes the admissions page
  // passes, so the two render the same control.
  const personProgramOptions = programOptions
    .filter((option) => option.value !== NONE_VALUE)
    .map((option) => ({ value: option.value, label: option.label }));
  const personStartingDateOptions = intakeDateOptions.map((intake) => ({
    value: intake.start,
    label: intake.legacy ? `${formatDate(intake.start)} (existing)` : formatDate(intake.start),
  }));
  const personAvailableAddOns = availableAddOns.map((addOn) => ({
    id: addOn.id,
    label: addOn.label,
    description: addOn.description,
  }));

  const agencyFallback = linkedAgent ? {
    agentFirstName: String(linkedAgent.mainContact?.name || linkedAgent.agent_name || '').trim().split(/\s+/)[0] || '',
    agentLastName: String(linkedAgent.mainContact?.name || linkedAgent.agent_name || '').trim().split(/\s+/).slice(1).join(' '),
    agencyCompanyName: String(linkedAgent.company_name || '').trim(),
    agentPhone: String(linkedAgent.mainContact?.phone || linkedAgent.phone || '').trim(),
    agentEmail: String(linkedAgent.mainContact?.email || linkedAgent.email || '').trim(),
  } : null;
  const agencyFallbackPerson = agencyFallback;
  const linkedAgencyLegalName = linkedAgent?.company_name || '';
  const linkedAgencyPreferredName = String(linkedAgent?.preferredName || '').trim();

  /* ══════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════ */

  return (
    <PersonFieldsProvider values={editing ? form?.custom_fields : student.custom_fields} editing={editing} onChange={(key, value) => setForm((prev) => ({ ...prev, custom_fields: { ...prev.custom_fields, [key]: value } }))}>
    <div className="space-y-6 animate-fade-in">
      {/* Top bar: back + edit/save buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate('/admin/students')} className="text-[#062B1A] -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to students
        </Button>
        {!editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <PortalEmailComposer
              audience="students"
              recipients={[student]}
              buttonLabel="Email student"
              buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
            />
            <Button
              variant="outline"
              onClick={() => {
                setDeleteAdmissionRecord(false);
                setConfirmingDelete(true);
              }}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
            <ProfileLayoutToolbar controller={layoutController} />
            <PersonFieldsToolbar disabled={editing || layoutController.editMode} />
            {!layoutController.editMode && (
              <Button onClick={startEditing} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                <PencilLine className="w-4 h-4 mr-2" /> Edit Student
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={cancelEditing} disabled={saving}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={confirmingDelete} onOpenChange={(open) => !deleting && setConfirmingDelete(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {displayText(student.name, 'this student')}?</DialogTitle>
            <DialogDescription>
              This permanently removes the student record, their login account, notifications,
              submissions, grades, issued PDF documents and all personal data including the SIN.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {linkedAdmissionId && (
            <label className="flex items-start gap-3 rounded-lg border border-[rgba(6,43,26,0.12)] bg-[#F7F8FA] p-3 text-sm text-[#062B1A]">
              <input
                type="checkbox"
                checked={deleteAdmissionRecord}
                onChange={(event) => setDeleteAdmissionRecord(event.target.checked)}
                disabled={deleting}
                className="mt-1"
              />
              <span>
                <span className="block font-medium">Also delete linked admission record</span>
                <span className="block text-xs text-[#062B1A]/55">
                  Leave this unchecked to keep the admission record marked as student profile deleted.
                </span>
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmedDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {deleting ? 'Deleting...' : 'Delete student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Profile Header ─────────────────────────────── */}
      <ProfileHero
        avatar={(
          <Avatar className="h-24 w-24 border-4 border-white shadow-md">
            {formText(student.photo) && <AvatarImage src={formText(student.photo)} alt={displayText(student.name, 'Student')} />}
            <AvatarFallback className="bg-[#D4AF37] text-2xl font-bold text-[#062B1A]">
              {studentInitials(editing && form ? form.name : student.name)}
            </AvatarFallback>
          </Avatar>
        )}
      >
        {!editing ? (
          <>
            <h2 className="break-words text-2xl font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
              {displayText(student.name, 'Unnamed student')}
            </h2>
            <div className="flex items-center gap-2 break-all text-sm text-white/90">
              <Mail className="h-4 w-4 shrink-0" /> {displayText(student.email, 'No email')}
            </div>
            <div className="text-sm font-medium text-white">
              Student ID: {displayText(student.student_number, 'Not assigned')}
            </div>
          </>
        ) : (
          <div className="grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-white/90">Name</Label>
              <Input value={form.name} onChange={setText('name')} className={inputClass} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-white/90">Email</Label>
              <Input value={form.email} onChange={setText('email')} className={inputClass} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="student-number" className="text-xs text-white/90">Student ID</Label>
              <Input
                id="student-number"
                value={form.student_number}
                onChange={(event) => setForm((prev) => ({ ...prev, student_number: event.target.value.toUpperCase() }))}
                className={inputClass}
                maxLength={40}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-white/90">Photo URL</Label>
              <Input value={form.photo} onChange={setText('photo')} className={inputClass} placeholder="https://..." />
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Badge variant="outline" className="border-white/40 bg-white font-semibold text-[#062B1A]">
            Student
          </Badge>
          {editing ? (
            <Select value={form.enrollment_status} onValueChange={setSelect('enrollment_status')}>
              <SelectTrigger aria-label="Student status" className="h-7 w-auto min-w-[120px] gap-2 rounded-full border-white/40 bg-white/[0.15] px-3 text-xs font-semibold text-white">
                <SelectValue placeholder="Status not set" />
              </SelectTrigger>
              <SelectContent>
                {metaOptions([...new Set([...(meta.enrollment_status || []), student.enrollment_status].filter(Boolean))]).map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="border-white/40 bg-white/[0.15] text-white">
              {displayText(student.enrollment_status, 'Status not set')}
            </Badge>
          )}
          {sIsIntl && (
            <Badge variant="outline" className="border-white/40 bg-white/[0.15] text-white">
              <Globe2 className="mr-1 h-3 w-3" /> International
            </Badge>
          )}
          <ProfileFlagControl key={student.id || studentId} kind="student" recordId={student.id || studentId} />
        </div>
      </ProfileHero>

      <ProfileSectionGrid>
        <ProfileSection id="official_documents" controller={layoutController}>
          <OfficialDocumentsCard ownerType="student" ownerId={student.id || studentId} docTypes={studentDocumentDocTypes} />
        </ProfileSection>

        {/* The shared spine. These three are the same components the
            admissions page renders, from the same view model, so the two
            pages cannot drift apart again. */}
        <ProfileSection id="personal" controller={layoutController}>
          <PersonalSection
            title={layoutController.sectionById.personal?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            setAddressField={setPersonAddressField}
            countries={safeList(meta.countries)}
            sinMask={student.sinMask}
            revealedSin={revealedSin}
            onRevealSin={handleRevealSin}
            revealingSin={revealingSin}
          />
        </ProfileSection>

        <ProfileSection id="agency" controller={layoutController}>
          <AgencySection
            title={layoutController.sectionById.agency?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            agentOptions={agentOptions}
            onSelectAgency={(id) => setForm((prev) => selectAgency(prev, id, agents))}
            linkedAgencyName={linkedAgentName}
            linkedAgencyLegalName={linkedAgencyLegalName}
            linkedAgencyPreferredName={linkedAgencyPreferredName}
            agentFallback={agencyFallbackPerson}
            linkedAgencyHref={linkedAgentId ? `/admin/agencies/${linkedAgentId}` : ''}
          />
        </ProfileSection>

        <ProfileSection id="referral" controller={layoutController}>
          <ReferralSection
            title={layoutController.sectionById.referral?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
          />
        </ProfileSection>

        <ProfileSection id="program" controller={layoutController}>
          <ProgramSection
            enrollmentStatus={student.enrollment_status}
            enrollmentStatusDraft={payloadSelect(form?.enrollment_status)}
            enrollmentStatusOptions={metaOptions([...new Set([...(meta.enrollment_status || []), student.enrollment_status].filter(Boolean))])}
            onEnrollmentStatusChange={setSelect('enrollment_status')}
            title={layoutController.sectionById.program?.title}
            person={personView}
            form={personForm}
            editing={editing}
            setField={setPersonField}
            programOptions={personProgramOptions}
            startingDateOptions={personStartingDateOptions}
            availableAddOns={personAvailableAddOns}
            formatStart={formatDate}
            endDate={form?.end_date || student.end_date}
            englishProficiencyTypes={metaOptions(meta.english_proficiency_type)}
          />
        </ProfileSection>

        {/* The student sections. The admissions page renders this same
            component from the mirror it holds of this record. */}
        <StudentSections
          student={student}
          showGraduation={false}
          form={form}
          editing={editing}
          meta={meta}
          layoutController={layoutController}
          courses={courses}
          intakeDateOptions={intakeDateOptions}
          setText={setText}
          setSelect={setSelect}
          setGraduationStartDate={setGraduationStartDate}
          setGraduationEndDate={setGraduationEndDate}
          selectedProgramName={selectedProgramName}
          onTuitionPaymentsChanged={async () => {
            try {
              const res = await getStudent(studentId);
              setStudent(res.data);
              setForm((prev) => prev ? {
                ...prev,
                tuition_total: formText(res.data.tuition_total),
                tuition_paid: formText(res.data.tuition_paid),
              } : prev);
            } catch { /* the ledger card already surfaced the failure */ }
          }}
        />

        {showFrenchSection && (
          <ProfileSection id="french_program" controller={layoutController}>
            <FrenchProgramSection
              title={layoutController.sectionById.french_program?.title}
              person={personView}
              form={personForm}
              editing={editing}
              setField={setPersonField}
            />
          </ProfileSection>
        )}

        <ProfileSection id="language_tests" controller={layoutController}>
          <LanguageTestsCard targetType="student" targetId={student.id} />
        </ProfileSection>

        {/* ── Notes ──────────────────────────────────────── */}
        <ProfileSection id="notes" controller={layoutController}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
            <StickyNote className="w-4 h-4 text-[#D4AF37]" /> Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <NotesThread
            isSuperAdmin={currentUser?.role === 'admin' && currentUser?.is_super_admin === true}
            notes={noteHistory}
            currentUserId={currentUserId}
            onAdd={handleAddNote}
            onEdit={handleEditNote}
            onDelete={handleDeleteNote}
            deletingId={deletingNoteId}
            emptyText="No notes yet — add the first."
          />
        </CardContent>
      </Card>

        </ProfileSection>
      </ProfileSectionGrid>

      {/* ── Bottom save bar (edit mode) ────────────────── */}
      {editing && (
        <div className="flex justify-end gap-2 sticky bottom-4 bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-[rgba(6, 43, 26,0.08)] shadow-lg">
          <Button variant="outline" onClick={cancelEditing} disabled={saving}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
    </PersonFieldsProvider>
  );
}

export default function AdminStudentDetail() {
  return (
    <StudentDetailErrorBoundary>
      <AdminStudentDetailContent />
    </StudentDetailErrorBoundary>
  );
}
